import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-before-you-open-your-phone",
  title: "A prayer before you open your phone",
  h1: "A prayer before you open your phone",
  description:
    "Ten seconds before the unlock: a prayer for the gap between wanting and opening, with four passages on attention and appetite.",
  targets: [
    "prayer before using your phone",
    "prayer before social media",
    "christian prayer about screen time",
    "prayer to stop picking up my phone",
  ],
  published: "2026-09-03",
  standfirst:
    "Nobody decides to lose forty minutes. The hand moves, the thumb finds the app, and the decision was never made anywhere you could see it.",
  takeaways: [
    "Say it in the gap between reaching and opening. That gap is about a second long and it is the only place a decision can still happen.",
    "Matthew 6:21 runs the direction people do not expect: the heart follows the treasure, so attention is upstream of affection rather than downstream.",
    "This prayer is a habit, not an event. Its whole value is in being said many times badly rather than once well.",
  ],
  when: [
    "Every time, ideally. Realistically, the first few times you notice.",
    "When you have picked the phone up without any idea what for.",
    "Before the app you have already closed twice tonight.",
    "In the queue, in the lift, at the traffic light — the small unlocks nobody counts.",
  ],
  prayer: [
    "God, I am about to open this, and I do not actually know why. That is the honest position.",
    "Set a watch over what I am about to look at, and over what I am about to become while looking at it.",
    "If there is something here I need — a message, a thing I promised somebody — let me do that and put it down.",
    "If there is not, let me notice that quickly rather than in forty minutes.",
    "You have my attention now. Keep it for another second before I hand it to whoever is next.",
  ],
  short: "God, I do not know why I am opening this. Let me notice quickly.",
  honest: [
    "This prayer does not stop the reaching. The reaching is automatic, it has been trained for years, and a sentence will not untrain it — what the sentence can do is make the reach visible, which is a smaller and more achievable thing.",
    "It also is not a substitute for changing the arrangement. If the app is one tap away, you are asking a ten-second prayer to outperform a system built by people whose whole job is to win that second.",
    "And it does not make phones bad. Most of what is on there is fine. The problem is not the object, it is the number of times a day you consult it without having decided to.",
  ],
  verses: [
    {
      verse: {
        text: "Set a watch, Yahweh, before my mouth. Keep the door of my lips.",
        ref: "Psalm 141:3",
      },
      note: "A request for a guard on an exit — somebody posted at the door, checking what leaves. The image transfers exactly to a screen, with the direction reversed: a watch on what comes in. Note that the psalmist does not ask to be made the sort of person who never says the wrong thing. He asks for a sentry, because he knows he will not manage it alone.",
    },
    {
      verse: {
        text: "Turn my eyes away from looking at worthless things. Revive me in your ways.",
        ref: "Psalm 119:37",
      },
      note: "The verb is passive on purpose: turn my eyes, not help me look away. By the time the looking has started, the psalmist is not the one steering — an observation about attention that is three thousand years older than the technology it now describes best.",
    },
    {
      verse: {
        text: "for where your treasure is, there your heart will be also.",
        ref: "Matthew 6:21",
      },
      note: "The causation runs the direction people do not expect. Not put your heart somewhere and your attention will follow, but put your treasure — your time, your money, your looking — somewhere, and your heart will arrive after it. Which means what you spend an hour a night on is not a symptom of what you love. It is a cause.",
    },
    {
      verse: {
        text: "But put on the Lord Jesus Christ, and make no provision for the flesh, for its lusts.",
        ref: "Romans 13:14",
      },
      note: "Make no provision is a logistics phrase — do not lay in supplies for it, do not stock the cupboard. It is about arrangements made in advance rather than resistance in the moment, which is the same insight behind every effective thing anybody has ever done about a phone. The battle is lost or won at the level of what is within reach.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The gap is about one second",
      id: "the-gap-is-about-one-second",
    },
    {
      type: "p",
      text: "Between the reach and the unlock there is a moment where the whole thing is still reversible. It is short, it is easy to miss, and it is the only point in the sequence where you are in a position to do anything at all.",
    },
    {
      type: "p",
      text: "Everything after it — the app, the feed, the ninth minute — runs on rails. That is not a moral claim about you; it is a design achievement by people who are extremely good at their jobs.",
    },
    {
      type: "h2",
      text: "Make no provision",
      id: "make-no-provision",
    },
    {
      type: "p",
      text: "Romans 13:14 is about supply rather than willpower, and it is the sentence that most Christian advice about phones skips. Do not stock the cupboard. Do not keep it on the home screen, in the bedroom, in your hand during the hour you always lose.",
    },
    {
      type: "note",
      text: "A prayer said in the gap works far better when the gap has been artificially widened. Anything that costs more than one tap will do: a phone in another room, an app moved four screens deep, a lock somebody else set.",
    },
    {
      type: "h2",
      text: "Say it badly, often",
      id: "say-it-badly-often",
    },
    {
      type: "p",
      text: "This is the least impressive prayer on this site and the one most likely to change a week. Its value is entirely in repetition: a hundred half-hearted times beats one sincere time, because what is being built is a noticing rather than a resolution — and it works alongside {a limit you cannot dismiss} rather than instead of one.",
      links: {
        "a limit you cannot dismiss": "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "p",
      text: "And when you forget for three days and remember on the fourth, that is not a failure of the practice. That is the practice.",
    },
  ],
  faq: [
    {
      q: "What is a short prayer before using your phone?",
      a: '"God, I do not know why I am opening this. Let me notice quickly." Ten seconds, said in the gap between reaching and unlocking, and worth far more repeated badly than said once well.',
    },
    {
      q: "Which Bible verse is about screen time?",
      a: "None of them are, and any page claiming otherwise is stretching. The closest in substance are Psalm 119:37 on looking at worthless things, and Romans 13:14 on making no provision — which is about arrangements rather than willpower.",
    },
    {
      q: "Does praying before opening an app actually help?",
      a: "It makes the reach visible, which is most of the problem — almost nobody decides to lose forty minutes. It will not outperform an app that is one tap away, so it works best alongside friction you cannot dismiss.",
    },
  ],
  relatedPrayers: ["prayer-for-self-control", "prayer-for-focus"],
  related: ["screen-time-limits-dont-work", "christian-app-blocker-guide"],
  relatedTopics: [
    "bible-verses-about-self-control",
    "bible-verses-about-temptation",
  ],
  relatedPassages: ["romans-12-2"],
};
